/** Injection tokens for API client configuration. */

import { InjectionToken } from '@angular/core';
import type { LookupSearchConfig } from '../storage/lookup.types';

// ═══════════════════════════════════════════════════════════════════════════
// PRIMARY API TOKEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Base URL for the MFE's own/primary API requests. ALL MFEs use this for their own backend via provideApiConfig().
 *
 * Pattern:
 *
 * - Admin MFE: provideApiConfig({ baseUrl: adminApiUrl }) → API_BASE_URL
 * - Template MFE: provideApiConfig({ baseUrl: templateApiUrl }) → API_BASE_URL
 * - Assessment MFE: provideApiConfig({ baseUrl: assessmentApiUrl }) → API_BASE_URL
 */
export const API_BASE_URL = new InjectionToken<string>('API_BASE_URL');

// ═══════════════════════════════════════════════════════════════════════════
// SECONDARY/EXTERNAL API TOKENS
// Use these for additional APIs (FHIR) or cross-MFE calls
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Base URL for FHIR API. Secondary API used by Template, Assessment, Audit for FHIR operations. Use provideFhirConfig()
 * to provide this token.
 */
export const FHIR_API_BASE_URL = new InjectionToken<string>('FHIR_API_BASE_URL');

/** Base URL for Lookup API (port 7004). Shared lookup service used by all MFEs. */
export const LOOKUP_API_BASE_URL = new InjectionToken<string>('LOOKUP_API_BASE_URL');

/** Selects worker or paged API search for configured lookup tables. */
export const LOOKUP_SEARCH_CONFIG = new InjectionToken<LookupSearchConfig>('LOOKUP_SEARCH_CONFIG');

// ═══════════════════════════════════════════════════════════════════════════
// CROSS-MFE API TOKENS
// Use these when calling another MFE's backend API
// ═══════════════════════════════════════════════════════════════════════════

/** Base URL for Admin API (port 7001). Used when other MFEs need to call admin services. */
export const ADMIN_API_BASE_URL = new InjectionToken<string>('ADMIN_API_BASE_URL');

/**
 * Base URL for Template API (port 7005). Used by Assessment, Audit MFEs when they need to call Template services. Note:
 * Template MFE itself uses API_BASE_URL for its own backend.
 */
export const TEMPLATE_API_BASE_URL = new InjectionToken<string>('TEMPLATE_API_BASE_URL');

/**
 * Base URL for Assessment API (port 7003). Used by Audit MFE when it needs to call Assessment services. Note:
 * Assessment MFE itself uses API_BASE_URL for its own backend.
 */
export const ASSESSMENT_API_BASE_URL = new InjectionToken<string>('ASSESSMENT_API_BASE_URL');

/**
 * Base URL for Audit API (port 7002). Used when other MFEs need to call Audit services. Note: Audit MFE itself uses
 * API_BASE_URL for its own backend.
 */
export const AUDIT_API_BASE_URL = new InjectionToken<string>('AUDIT_API_BASE_URL');

/**
 * Base URL for Agent API (port 7006). Used by Assessment, Admin, and Template MFEs when calling the Symphony AI Agent
 * service.
 */
export const AGENT_API_BASE_URL = new InjectionToken<string>('AGENT_API_BASE_URL');

/** Base URL for Telehealth API (port 7008). Used by the Telehealth MFE for meeting management and related services. */
export const TELEHEALTH_API_BASE_URL = new InjectionToken<string>('TELEHEALTH_API_BASE_URL');

// ═══════════════════════════════════════════════════════════════════════════
// CACHE CONFIGURATION TOKENS
// ═══════════════════════════════════════════════════════════════════════════

/** Max entries for FHIR ETag cache (default 100). */
export const FHIR_CACHE_MAX_ENTRIES = new InjectionToken<number>('FHIR_CACHE_MAX_ENTRIES');

/** TTL in ms for FHIR cache entries (default 5 min). Set 0 to disable expiry. */
export const FHIR_CACHE_TTL_MS = new InjectionToken<number>('FHIR_CACHE_TTL_MS');

// ═══════════════════════════════════════════════════════════════════════════
// TENANT HEADER CONFIGURATION TOKENS
// ═══════════════════════════════════════════════════════════════════════════

/** Default Client ID for X-Client-Id header injection. Injected from NX_DEFAULT_CLIENT_ID environment variable. */
export const DEFAULT_CLIENT_ID = new InjectionToken<string>('DEFAULT_CLIENT_ID');

/** Default Vendor ID for X-Vendor-Id header injection. Injected from NX_DEFAULT_VENDOR_ID environment variable. */
export const DEFAULT_VENDOR_ID = new InjectionToken<string>('DEFAULT_VENDOR_ID');

// ═══════════════════════════════════════════════════════════════════════════
// ASSESSMENT CONTEXT TOKENS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Line of Business name for the current assessment context (e.g., 'Medicare Advantage'). Provided at the assessment
 * component level from rawMemberInfo.member?.lineOfBusiness. Injected by BaseControlWrapper to pass lobName to ICD
 * lookup API calls.
 */
export const LOB_NAME = new InjectionToken<string>('LOB_NAME');

/** Storage key for persisting offline assessment enablement override in developer settings */
export const STORAGE_KEY_OFFLINE_ASSESSMENT_OVERRIDE = 'symphony.offlineAssessment.override';

/** Global bridge key for cross-MFE offline assessment enablement sharing */
export const GLOBAL_OFFLINE_ASSESSMENT_KEY = '__SYMPHONY_ENABLE_OFFLINE_ASSESSMENT__';

/**
 * Resolves the effective offline assessment enablement state. Checks for a developer settings localStorage override
 * ('true' | 'false'), host shell globalThis bridge, falling back to the build/environment configuration default.
 *
 * @param environmentDefault Optional default value from environment configuration
 * @returns Boolean indicating whether offline assessment capabilities are active
 */
export function isOfflineAssessmentEnabled(environmentDefault: boolean = false): boolean {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return environmentDefault;
  }

  const override = localStorage.getItem(STORAGE_KEY_OFFLINE_ASSESSMENT_OVERRIDE);
  if (override === 'true') {
    return true;
  }
  if (override === 'false') {
    return false;
  }

  // Cross-MFE bridge: Host shell sets this on startup from its environment.enableOfflineAssessment
  const globalFlag = (globalThis as Record<string, unknown>)[GLOBAL_OFFLINE_ASSESSMENT_KEY];
  if (typeof globalFlag === 'boolean') {
    return globalFlag;
  }

  return environmentDefault;
}

/** Sets the global offline assessment flag for cross-MFE consumption */
export function setOfflineAssessmentGlobal(enabled: boolean): void {
  (globalThis as Record<string, unknown>)[GLOBAL_OFFLINE_ASSESSMENT_KEY] = enabled;
}

/**
 * Feature flag token to enable or disable offline assessment capabilities across MFEs. When false, offline storage
 * strategies, pre-downloads, and offline UI behaviors are bypassed.
 */
export const ENABLE_OFFLINE_ASSESSMENT = new InjectionToken<boolean>('ENABLE_OFFLINE_ASSESSMENT', {
  providedIn: 'root',
  factory: () => isOfflineAssessmentEnabled(false),
});
